import { useTranslation } from "react-i18next";
import {
  CheckCircleFilled,
  PlayCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import {
  EducationType,
  EnrollmentStatus,
} from "../../types/client-education-detail.types";
import type {
  EducationDetailDto,
  EnrollmentDto,
} from "../../types/client-education-detail.types";
import sectionStyles from "./sections.module.scss";
import styles from "./TrainingStages.module.scss";

type StageState = "done" | "active" | "upcoming" | "failed";

interface Stage {
  key: string;
  title: string;
  description: string;
  state: StageState;
  sectionId: string;
}

function buildStages(
  education: EducationDetailDto,
  enrollment: EnrollmentDto | null,
  t: (k: string) => string,
): Stage[] {
  const isContent =
    education.type === EducationType.UNIT ||
    education.type === EducationType.NONUNIT;
  const middleLabel = isContent
    ? t("clientEducations.trainingStages.middleContent")
    : t("clientEducations.trainingStages.middleClass");
  const middleDesc = isContent
    ? t("clientEducations.trainingStages.middleContentDesc")
    : t("clientEducations.trainingStages.middleClassDesc");
  const middleSection = isContent
    ? "content"
    : education.type === EducationType.ONLINE
      ? "virtualClassrooms"
      : "scheduling";

  // Kayıt aşaması — yalnızca enrollment.status >= ENROLLED ise "onaylandı"
  // mesajını veriyoruz. Aksi halde (REQUESTED / APPROVED_BRANCH_MANAGER /
  // APPROVED_EDUCATION_SUPERVISOR) hâlâ onay sürecinde olduğu için "kayıt
  // bekleniyor / onay bekleniyor" daha doğru. Önceden enrollment objesi var
  // diye direkt "onaylandı" yazılıyor ve içerik açılmadan önce kullanıcıyı
  // yanıltıyordu.
  const isEnrolledOrLater =
    !!enrollment && enrollment.status >= EnrollmentStatus.ENROLLED;
  const stages: Stage[] = [
    {
      key: "enrolled",
      title: t("clientEducations.trainingStages.enrollment"),
      description: !enrollment
        ? t("clientEducations.trainingStages.enrollmentPending")
        : isEnrolledOrLater
          ? t("clientEducations.trainingStages.enrollmentApproved")
          : t("clientEducations.trainingStages.enrollmentApprovalPending"),
      state: isEnrolledOrLater ? "done" : "active",
      sectionId: "hero",
    },
  ];

  if (!enrollment) {
    return stages;
  }

  // Yönetici "tamamlamadı / devamsızlıktan kaldı" işaretledi: timeline'da
  // Kayıt aşamasını "done" tut (kullanıcı kayıt olmuştu), kalan tüm aşamaları
  // "failed" göster — banner ayrıca açıklama verir.
  if (enrollment.status === EnrollmentStatus.NOT_COMPLETED) {
    stages.push({
      key: "middle",
      title: middleLabel,
      description: t("clientEducations.trainingStages.notCompletedDesc"),
      state: "failed",
      sectionId: middleSection,
    });
    if (education.exam?.id) {
      stages.push({
        key: "exam",
        title: t("clientEducations.trainingStages.exam"),
        description: t("clientEducations.trainingStages.notCompletedDesc"),
        state: "failed",
        sectionId: "exam",
      });
    }
    if (education.survey) {
      stages.push({
        key: "survey",
        title: t("clientEducations.trainingStages.survey"),
        description: t("clientEducations.trainingStages.notCompletedDesc"),
        state: "failed",
        sectionId: "survey",
      });
    }
    stages.push({
      key: "certificate",
      title: education.certificateId
        ? t("clientEducations.trainingStages.certificate")
        : t("clientEducations.trainingStages.completion"),
      description: t("clientEducations.trainingStages.notCompletedDesc"),
      state: "failed",
      sectionId: "certificate",
    });
    return stages;
  }

  const st = enrollment.status;
  const progress = enrollment.progressPercent ?? 0;

  // Eğitim başlangıcı / orta aşama
  stages.push({
    key: "middle",
    title: middleLabel,
    description: middleDesc,
    state:
      st >= EnrollmentStatus.CONTENT_COMPLETED
        ? "done"
        : progress > 0 || st === EnrollmentStatus.ENROLLED
          ? "active"
          : "upcoming",
    sectionId: middleSection,
  });

  if (education.exam?.id) {
    stages.push({
      key: "exam",
      title: t("clientEducations.trainingStages.exam"),
      description: t("clientEducations.trainingStages.examDescription"),
      state:
        st >= EnrollmentStatus.EXAM_COMPLETED
          ? "done"
          : st === EnrollmentStatus.CONTENT_COMPLETED ||
              st === EnrollmentStatus.EXAM_STARTED ||
              st === EnrollmentStatus.EXAM_FAILED
            ? "active"
            : "upcoming",
      sectionId: "exam",
    });
  }

  if (education.survey) {
    stages.push({
      key: "survey",
      title: t("clientEducations.trainingStages.survey"),
      description: t("clientEducations.trainingStages.surveyDescription"),
      state:
        st === EnrollmentStatus.COMPLETED
          ? "done"
          : st === EnrollmentStatus.EXAM_COMPLETED ||
              st === EnrollmentStatus.SURVEY_STARTED
            ? "active"
            : "upcoming",
      sectionId: "survey",
    });
  }

  stages.push({
    key: "certificate",
    title: education.certificateId
      ? t("clientEducations.trainingStages.certificate")
      : t("clientEducations.trainingStages.completion"),
    description: education.certificateId
      ? t("clientEducations.trainingStages.certificateDescription")
      : t("clientEducations.trainingStages.completionDescription"),
    state:
      st === EnrollmentStatus.COMPLETED
        ? education.certificateId
          ? "active"
          : "done"
        : "upcoming",
    sectionId: "certificate",
  });

  return stages;
}

interface TrainingStagesProps {
  education: EducationDetailDto;
  enrollment: EnrollmentDto | null;
  onJump?: (sectionId: string) => void;
}

/**
 * Eğitim sürecindeki aşamaları yatay olarak gösterir:
 * Kayıt → İçerik/Katılım → Sınav → Anket → Sertifika.
 * Her aşama tıklanabilir; ilgili sayfa bölümüne kaydırır.
 */
export function TrainingStages({
  education,
  enrollment,
  onJump,
}: TrainingStagesProps) {
  const { t } = useTranslation();
  const stages = buildStages(education, enrollment, t);

  return (
    <section className={`${sectionStyles.section} ${styles.wrap}`}>
      <div className={sectionStyles.header}>
        <h2>{t("clientEducations.trainingStages.title")}</h2>
      </div>
      <ol className={styles.list}>
        {stages.map((s, idx) => {
          const isLast = idx === stages.length - 1;
          return (
            <li
              key={s.key}
              className={`${styles.item} ${styles[`state_${s.state}`]}`}
            >
              <button
                type="button"
                className={styles.btn}
                onClick={() => onJump?.(s.sectionId)}
              >
                <span className={styles.iconWrap}>
                  {s.state === "done" ? (
                    <CheckCircleFilled />
                  ) : s.state === "active" ? (
                    <PlayCircleFilled />
                  ) : s.state === "failed" ? (
                    <CloseCircleFilled />
                  ) : (
                    <ClockCircleOutlined />
                  )}
                </span>
                <span className={styles.body}>
                  <span className={styles.title}>{s.title}</span>
                  <span className={styles.desc}>{s.description}</span>
                </span>
              </button>
              {!isLast && <span className={styles.connector} />}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
